import React, { useState } from 'react'
import { App, Button, Form, Modal, Popconfirm, Segmented, Table } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, SectionCard } from '../../theme/listkit'
import { MemberSelect } from '../notification_rules/MemberSelect'
import { useClassroomSettings } from '../group_chapters/useClassroomSettings'
import { MODE_LABELS, normalizeYears } from '../group_chapters/rollout'
import { useGroupsAudience, type GroupsAudienceState, type GroupsTester } from './useGroupsAudience'

/** Every card on this page is capped: the owner's display is 3440px wide (admin-ultrawide-display). */
const CARD = { maxWidth: 720 } as const
const muted: React.CSSProperties = { fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub, lineHeight: 1.5 }

/** Narrowest to widest. "Off" is a kill switch, not just the narrowest audience — see audienceNeedsConfirm. */
export const AUDIENCE_STATES: GroupsAudienceState[] = ['off', 'admins', 'testers', 'everyone']

export const AUDIENCE_LABELS: Record<GroupsAudienceState, string> = {
  off: 'Off',
  admins: 'Admins only',
  testers: 'Admins + testers',
  everyone: 'Everyone',
}

/** Help line under the segmented control — copied verbatim from docs/groups/COMPONENTS-COPY.md. */
export const AUDIENCE_HELP: Record<GroupsAudienceState, string> = {
  off: 'Groups and Classrooms are off for everyone, admins included. Use this only as a kill switch.',
  admins: 'Only admins see and use Groups. Testers stay on the list but see nothing. Members see nothing.',
  testers: 'Admins and the testers below use Groups exactly as members will. Everyone else sees nothing.',
  everyone: 'Every signed-in member can browse, join and use interest groups. Classrooms still follow the Classrooms tab.',
}

/** True when `to` shows Groups to more people than `from`, on the off→admins→testers→everyone ladder. */
export function isAudienceWidening(from: GroupsAudienceState, to: GroupsAudienceState): boolean {
  return AUDIENCE_STATES.indexOf(to) > AUDIENCE_STATES.indexOf(from)
}

/**
 * Widening always confirms. "Off" also confirms even though it is the
 * narrowest state, because it takes Groups away from admins too — the one
 * narrowing that is NOT a one-tap brake (spec §2a).
 */
export function audienceNeedsConfirm(from: GroupsAudienceState, to: GroupsAudienceState): boolean {
  if (from === to) return false
  return isAudienceWidening(from, to) || to === 'off'
}

export interface AudienceConfirmCopy {
  title: string
  content: string
  okText: string
}

/**
 * The question asked before moving to `to`. Only called when
 * audienceNeedsConfirm() is true. `testerCount` is `null` when the
 * `groups_testers` read itself failed (MA-F3-02, final-review round 3) —
 * never render a claimed count ("0 testers…") drawn from a read that never
 * succeeded, since the real (unreadable) list is still admitted once the
 * owner confirms.
 */
export function audienceConfirm(to: GroupsAudienceState, testerCount: number | null): AudienceConfirmCopy {
  switch (to) {
    case 'testers': {
      // MA-F2-03 (final-review round 2): the "next time they open the app"
      // promise is unqualified — CLAUDE.md's OPEN gotcha (MA-F1-03/SR-4) and
      // the on-device repro in docs/groups/progress-audience-integration.md
      // both found a tester's first sign-in AND a relaunch can still miss
      // the grant; only an explicit sign-out/sign-in reliably picked it up.
      // Say so here instead of promising something the server can't back.
      const who = testerCount === null ? 'Your testers' : `${testerCount} testers`
      return {
        title: 'Let your testers in?',
        content: `${who} will see Groups (interest groups, and their own class's Classroom) the next time they open the app. If they still don't see it, have them sign out and back in.`,
        okText: 'Open',
      }
    }
    case 'everyone':
      return {
        title: 'Open Groups to every member?',
        content: 'Every member will see Groups: the tab, the Home card, joining, creating and ideas. Classrooms still follow the Classrooms tab.',
        okText: 'Open',
      }
    case 'off':
      return {
        title: 'Turn Groups off for everyone?',
        content: 'Groups and Classrooms disappear for everyone, admins included. Nothing is deleted.',
        okText: 'Turn off',
      }
    case 'admins':
      // GS-5: turning the master on reopens pilot/everyone Classrooms at once.
      return {
        title: 'Turn Groups back on?',
        content: 'Admins can use Groups again. Interest groups stay hidden from everyone else until you choose Admins + testers or Everyone; Classrooms reopen to whoever the Classrooms tab allows.',
        okText: 'Turn on',
      }
  }
}

/** "Pilot (1996, 2001, 2016)" · "Admins & testers" · "Off" · "Everyone" — the Classrooms half of the audience strip. */
export function classroomsAudienceLabel(mode: string | null, pilotYears: readonly string[] | null, unreadable: boolean): string {
  if (unreadable || !mode) return 'Unreadable'
  if (mode === 'pilot') {
    const years = normalizeYears(pilotYears ?? [])
    return years.length ? `Pilot (${years.join(', ')})` : 'Pilot (no class years yet)'
  }
  return MODE_LABELS[mode as keyof typeof MODE_LABELS] ?? mode
}

/**
 * "Who can use Groups" — the ONE control for interest-group visibility
 * (public.groups_rollout + feature_flags.groups_enabled, written atomically by
 * admin_set_groups_access) and the named "Groups testers" who preview it
 * before Everyone. Replaces the old "Groups" / "Groups browsing" switches on
 * this page: those rows are now server-owned (see page.tsx).
 */
export function GroupsAudienceCard() {
  const { message, modal } = App.useApp()
  const audience = useGroupsAudience()
  const classroom = useClassroomSettings()
  const [saving, setSaving] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [addIds, setAddIds] = useState<string[]>([])
  const [adding, setAdding] = useState(false)

  async function write(next: GroupsAudienceState) {
    setSaving(true)
    try {
      await audience.setState(next)
      message.success('Saved. Open apps catch up the next time they load.')
    } catch (e) {
      message.error(`Couldn’t save: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSaving(false)
    }
  }

  function onChange(next: GroupsAudienceState) {
    if (!audience.state || next === audience.state) return
    if (!audienceNeedsConfirm(audience.state, next)) {
      void write(next)
      return
    }
    const copy = audienceConfirm(next, audience.testersUnreadable ? null : audience.testers.length)
    modal.confirm({
      title: copy.title,
      content: copy.content,
      okText: copy.okText,
      cancelText: 'Cancel',
      okButtonProps: next === 'off' ? { danger: true } : undefined,
      onOk: () => write(next),
    })
  }

  async function addTesters() {
    if (addIds.length === 0) return
    setAdding(true)
    try {
      await audience.addTesters(addIds)
      message.success(`Added ${addIds.length} tester${addIds.length === 1 ? '' : 's'}`)
      setAddOpen(false)
      setAddIds([])
    } catch (e) {
      message.error(`Couldn’t add ${addIds.length === 1 ? 'that tester' : 'those testers'}: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setAdding(false)
    }
  }

  async function removeTester(t: GroupsTester) {
    try {
      await audience.removeTester(t.user_id)
      message.success(`${t.name} is no longer a Groups tester`)
    } catch (e) {
      message.error(`Couldn’t remove ${t.name}: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  const strip = `Interest groups: ${audience.state ? AUDIENCE_LABELS[audience.state] : 'Unreadable'} · Classrooms: ${classroomsAudienceLabel(classroom.settings?.mode ?? null, classroom.settings?.pilot_years ?? null, classroom.unreadable)}`

  return (
    <>
      {/* SA-02 (fix round 1): no subtitle — spec §2a specifies none for this
          card (only the old "Groups settings" card below has one), and
          "within seconds" contradicted this card's own success toast
          ("...the next time they load") and tester help text ("...the
          next time they open the app"). admin_set_groups_access touches
          only feature_flags.groups_enabled and groups_rollout.mode, so a
          move from Admins only to Testers/Everyone has no other groups_*
          row for Realtime to push — there is no "seconds" claim to make. */}
      <SectionCard title="Who can use Groups" style={{ ...CARD, marginBottom: 16 }}>
        {audience.loading ? (
          <div style={muted}>Reading who can use Groups…</div>
        ) : audience.unreadable || !audience.state ? (
          <div>
            <div style={{ marginBottom: 6 }}>
              <Pill tone="red">Unreadable</Pill>
            </div>
            <div style={muted}>Can’t read who can use Groups yet. The database update for this card may not be applied.</div>
            <Button size="small" style={{ marginTop: 10 }} onClick={() => void audience.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <>
            <div data-testid="groups-audience-strip" style={{ ...muted, marginBottom: 12 }}>
              {strip}
            </div>
            <Segmented<GroupsAudienceState>
              data-testid="groups-audience-mode"
              block
              value={audience.state}
              disabled={saving}
              options={AUDIENCE_STATES.map((s) => ({ label: AUDIENCE_LABELS[s], value: s }))}
              onChange={onChange}
            />
            <div data-testid="groups-audience-help" style={{ ...muted, marginTop: 10 }}>
              {AUDIENCE_HELP[audience.state]}
            </div>
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Groups testers"
        sub={
          // MA-F2-03 (final-review round 2): see the confirm dialog's own
          // comment above — the sign-out/back-in workaround belongs in this
          // copy too, not only in ROLLOUT.md, since this is the help text an
          // owner actually sees while adding testers.
          "Testers use Groups exactly like a member would, and see their own class's Classroom while Classrooms is in Admins & testers or Pilot. Use test accounts or trusted alumni — what they post is real. Changes reach their phone the next time they open the app. If they still don't see it, have them sign out and back in."
        }
        action="Add testers"
        onAction={() => setAddOpen(true)}
        style={{ ...CARD, marginBottom: 16 }}
      >
        {audience.state === 'admins' && (
          <div data-testid="groups-testers-parked-note" style={{ ...muted, fontStyle: 'italic', marginBottom: 10 }}>
            Testers are parked while Groups is Admins only.
          </div>
        )}
        {audience.testersUnreadable ? (
          // MA-F3-02 (final-review round 3): a failed read must never look
          // like "no testers" — the widening confirm above already avoids
          // claiming a count, but the empty state here was ALSO drawn on a
          // failed read, hiding real testers (and their Tester pills in
          // GroupMembersTable) that are still on the list.
          <div>
            <div style={{ marginBottom: 6 }}>
              <Pill tone="red">Unreadable</Pill>
            </div>
            <div data-testid="groups-testers-unreadable" style={muted}>
              Couldn’t read the testers list. Any testers already on it are unaffected and still see Groups.
            </div>
            <Button size="small" style={{ marginTop: 10 }} onClick={() => void audience.refetch()}>
              Try again
            </Button>
          </div>
        ) : audience.testers.length === 0 ? (
          <div data-testid="groups-testers-empty" style={muted}>
            No testers yet.
          </div>
        ) : (
          <Table<GroupsTester> dataSource={audience.testers} rowKey="user_id" pagination={false} size="small">
            <Table.Column<GroupsTester> dataIndex="name" title="Name" />
            <Table.Column<GroupsTester> dataIndex="class_year" title="Class" render={(v: string | null) => v ?? '—'} />
            <Table.Column<GroupsTester>
              dataIndex="created_at"
              title="Added"
              render={(v: string) => new Date(v).toLocaleDateString()}
            />
            <Table.Column<GroupsTester>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, t) => (
                <Popconfirm
                  title={`Remove ${t.name} as a tester? They lose Groups access right away.`}
                  okText="Remove"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => removeTester(t)}
                >
                  <Button size="small" danger data-testid={`groups-testers-remove-${t.user_id}`}>
                    Remove
                  </Button>
                </Popconfirm>
              )}
            />
          </Table>
        )}
      </SectionCard>

      <Modal
        title="Add testers"
        open={addOpen}
        onCancel={() => {
          setAddOpen(false)
          setAddIds([])
        }}
        onOk={addTesters}
        okText="Add"
        confirmLoading={adding}
        okButtonProps={{ disabled: addIds.length === 0 }}
      >
        <Form layout="vertical">
          <Form.Item label="Members">
            <MemberSelect value={addIds} onChange={setAddIds} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
